import {
  ArrowLeft,
  Calendar,
  Check,
  Copy,
  CreditCard,
  Loader2,
  MapPin,
  QrCode,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import CampoCupom from '@/components/checkform/CampoCupom';
import AceiteTermosCondicoes from '@/components/legal/AceiteTermosCondicoes';
import { Card } from '@/components/ui/card';
import UltimasVagasBadge from '@/components/ui/UltimasVagasBadge';

import {
  aplicarMascaraCpf,
  aplicarMascaraWhatsapp,
} from './EventoEspecialModel';
import { useEventoEspecialViewModel } from './useEventoEspecialViewModel';

const REAIS = (c) =>
  (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = (d) =>
  new Date(d).toLocaleString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });

export default function EventoEspecialView() {
  const {
    evento,
    carregando,
    naoEncontrado,
    dados,
    atualizarDado,
    cartao,
    atualizarCartao,
    metodoPagamento,
    setMetodoPagamento,
    erros,
    enviando,
    erroGlobal,
    resultado,
    confirmado,
    termosAceitos,
    setTermosAceitos,
    enviarCompra,
    copiarPix,
    pixCopiado,
    cupom,
    setCupom,
    validarCupom,
  } = useEventoEspecialViewModel();

  if (carregando) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#fffafb] to-[#fff5f7]">
        <Loader2 size={32} className="animate-spin text-[#ff2d6c]" />
      </div>
    );
  }

  if (naoEncontrado || !evento) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#fffafb] to-[#fff5f7] px-4">
        <Card className="text-center p-8 max-w-md shadow-xl border-slate-100/80 rounded-3xl">
          <p className="text-sm text-slate-500 font-medium">
            Esse evento não foi encontrado ou não está mais disponível.
          </p>
          <Link
            to="/"
            className="inline-block mt-6 text-xs font-black text-[#ff2d6c] uppercase tracking-widest hover:opacity-80 transition"
          >
            Voltar ao início
          </Link>
        </Card>
      </div>
    );
  }

  if (resultado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fffafb] to-[#fff5f7] flex items-center justify-center px-4 py-12">
        <Card className="max-w-md w-full text-center p-8 space-y-6 shadow-2xl border-slate-100/80 rounded-3xl">
          {confirmado ? (
            <>
              <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
                <Check size={28} className="text-green-600" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-black text-slate-900">
                  Pagamento confirmado!
                </h2>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Sua vaga em{' '}
                  <strong className="text-slate-700">"{evento.title}"</strong>{' '}
                  está garantida. Enviamos um e-mail de confirmação com os
                  detalhes.
                </p>
              </div>
            </>
          ) : resultado.pixQrCode ? (
            <>
              <div className="space-y-1">
                <h2 className="text-xs font-black text-[#ff2d6c] uppercase tracking-widest">
                  Quase lá!
                </h2>
                <p className="text-base font-black text-slate-900">
                  Pague com PIX para garantir sua vaga
                </p>
              </div>

              <div className="p-4 bg-white inline-block rounded-2xl shadow-md border border-slate-100 mx-auto">
                <img
                  src={
                    resultado.pixQrCode.startsWith('data:image')
                      ? resultado.pixQrCode
                      : `data:image/png;base64,${resultado.pixQrCode}`
                  }
                  alt="QR Code PIX"
                  className="w-48 h-48 mx-auto"
                />
              </div>

              <button
                onClick={copiarPix}
                className="inline-flex items-center justify-center gap-2 text-xs font-black uppercase tracking-widest text-white bg-[#ff2d6c] rounded-full w-full py-3.5 hover:bg-[#e02059] transition shadow-lg shadow-pink-500/20"
              >
                <Copy size={14} />{' '}
                {pixCopiado === true ? 'Código copiado' : 'Copiar código PIX'}
              </button>

              {/* Em webview de app a cópia pode ser bloqueada; sem o código à
                  vista não há como pagar pelo mesmo celular. */}
              {pixCopiado === false && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-left">
                  <p className="text-[11px] font-black uppercase tracking-wide text-amber-900">
                    Não foi possível copiar automaticamente
                  </p>
                  <p className="mt-1 text-[11px] font-semibold text-amber-800">
                    Toque e segure o código abaixo para copiar:
                  </p>
                  <p className="mt-2 max-h-28 overflow-y-auto break-all rounded-xl border border-amber-200 bg-white p-2.5 text-[11px] text-slate-700 select-all">
                    {resultado.pixCopiaECola}
                  </p>
                </div>
              )}
              <p className="text-[11px] text-slate-400">
                Assim que o pagamento for detectado, a confirmação acontecerá
                automaticamente nesta tela.
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-pink-50 rounded-2xl flex items-center justify-center mx-auto">
                <Loader2 size={28} className="text-[#ff2d6c] animate-spin" />
              </div>
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">
                Processando seu pagamento...
              </h2>
            </>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fffafb] to-[#fff5f7] py-12 px-4 flex flex-col items-center justify-center">
      <div className="max-w-4xl w-full space-y-6">
        {/* Botão de voltar */}
        <div className="flex justify-start">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-[#ff2d6c] transition-colors"
          >
            <ArrowLeft size={14} /> Voltar ao Início
          </Link>
        </div>

        {/* Layout Principal em Duas Colunas */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* Coluna da Esquerda: Imagem do Evento */}
          <div className="w-full lg:col-span-4 order-1">
            <div className="w-full rounded-2xl overflow-hidden shadow-xl border border-slate-200/60 bg-gradient-to-b from-slate-100 to-slate-50 flex items-center justify-center min-h-96 lg:min-h-[500px] relative">
              {evento.coverImageBase64 ? (
                <img
                  src={evento.coverImageBase64}
                  alt={evento.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center gap-3 text-slate-300 p-6">
                  <Sparkles size={48} className="text-pink-100" />
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                    Capa do livro
                  </p>
                </div>
              )}
              <span className="absolute bottom-4 left-4 bg-[#ff2d6c] text-white text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest">
                Evento Especial
              </span>
            </div>
          </div>

          {/* Coluna da Direita: Especificações + Formulário de Checkout */}
          <div className="w-full space-y-6 lg:col-span-8 order-2">
            {/* Card Único com Informações + Formulário */}
            <Card className="p-6 md:p-8 border-slate-100 shadow-xl rounded-3xl bg-white space-y-6">
              {/* Informações de Texto */}
              <div className="w-full space-y-3 text-center md:text-left">
                <span className="inline-block text-[10px] font-black uppercase tracking-widest text-[#ff2d6c] bg-pink-50 px-2.5 py-1 rounded-md">
                  Evento Especial
                </span>
                <h1 className="text-2xl md:text-3xl font-black text-slate-950 tracking-tight leading-tight">
                  {evento.title}
                </h1>
                {evento.description && (
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {evento.description}
                  </p>
                )}

                <hr className="border-slate-200/60 my-2" />

                <div className="space-y-2 text-xs text-slate-600 font-semibold flex flex-col items-center md:items-start">
                  <span className="flex items-center gap-2">
                    <Calendar size={15} className="text-[#ff2d6c]" />{' '}
                    {fmtData(evento.startDate)}
                  </span>
                  <span className="flex items-center gap-2">
                    <MapPin size={15} className="text-[#ff2d6c]" />{' '}
                    {evento.location}
                  </span>
                </div>

                <div className="pt-2">
                  <span className="text-[10px] text-slate-400 font-bold uppercase block tracking-wider">
                    Investimento
                  </span>
                  <div className="flex items-baseline gap-2 text-slate-900">
                    <p className="text-3xl font-black">
                      {REAIS(evento.amountInCents)}
                    </p>

                    {/* Valor Extra ao lado */}
                    {evento.addOns && evento.addOns.length > 0 && (
                      <span className="text-sm font-bold text-blue-600">
                        +{' '}
                        {REAIS(
                          evento.addOns.reduce(
                            (sum, a) => sum + a.priceInCents,
                            0,
                          ),
                        )}
                      </span>
                    )}
                  </div>

                  {/* Aviso do Valor Extra */}
                  {evento.addOns && evento.addOns.length > 0 && (
                    <p className="text-xs text-slate-500 mt-2 italic">
                      {evento.addOns.map((a) => a.description).join(' • ')}
                    </p>
                  )}

                  {!evento.esgotado && evento.ultimasVagas && (
                    <div className="mt-4 pt-4 border-t border-slate-100 flex justify-center md:justify-start">
                      <UltimasVagasBadge
                        variant="solid"
                        vagasRestantes={evento.vagasRestantes}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Formulário — gera o PIX (ou cobrança) depois do envio */}
              {evento.esgotado ? (
                <div className="text-center py-6">
                  <p className="text-sm text-slate-500 font-black uppercase tracking-wide">
                    Vagas esgotadas para este evento.
                  </p>
                </div>
              ) : (
                <div>
                  <form onSubmit={enviarCompra} className="space-y-5">
                    <div className="border-b border-slate-100 pb-2">
                      <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                        1. Preencha seus dados
                      </h2>
                    </div>

                    {/* Nome Completo */}
                    <div>
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1.5">
                        Nome completo
                      </label>
                      <input
                        value={dados.nome}
                        onChange={(e) => atualizarDado('nome', e.target.value)}
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#ff2d6c]/10 focus:border-[#ff2d6c] bg-slate-50/50"
                      />
                      {erros.nome && (
                        <p className="text-xs text-red-500 mt-1 font-medium">
                          {erros.nome}
                        </p>
                      )}
                    </div>

                    {/* Email e Whatsapp */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1.5">
                          E-mail
                        </label>
                        <input
                          type="email"
                          value={dados.email}
                          onChange={(e) =>
                            atualizarDado('email', e.target.value)
                          }
                          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#ff2d6c]/10 focus:border-[#ff2d6c] bg-slate-50/50"
                        />
                        {erros.email && (
                          <p className="text-xs text-red-500 mt-1 font-medium">
                            {erros.email}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1.5">
                          WhatsApp
                        </label>
                        <input
                          value={dados.whatsapp}
                          onChange={(e) =>
                            atualizarDado(
                              'whatsapp',
                              aplicarMascaraWhatsapp(e.target.value),
                            )
                          }
                          placeholder="(99) 99999-9999"
                          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#ff2d6c]/10 focus:border-[#ff2d6c] bg-slate-50/50"
                        />
                        {erros.whatsapp && (
                          <p className="text-xs text-red-500 mt-1 font-medium">
                            {erros.whatsapp}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* CPF */}
                    <div>
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1.5">
                        CPF
                      </label>
                      <input
                        value={dados.cpf}
                        onChange={(e) =>
                          atualizarDado(
                            'cpf',
                            aplicarMascaraCpf(e.target.value),
                          )
                        }
                        placeholder="000.000.000-00"
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#ff2d6c]/10 focus:border-[#ff2d6c] bg-slate-50/50"
                      />
                      {erros.cpf && (
                        <p className="text-xs text-red-500 mt-1 font-medium">
                          {erros.cpf}
                        </p>
                      )}
                    </div>

                    {/* Método de Pagamento */}
                    {!evento.pixOnly && (
                      <div className="space-y-3 pt-2">
                        <div className="border-b border-slate-100 pb-2">
                          <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                            2. Escolha a forma de pagamento
                          </h2>
                        </div>
                        <div className="grid gap-3 grid-cols-2">
                          <button
                            type="button"
                            onClick={() => setMetodoPagamento('pix')}
                            className={`flex items-center justify-center gap-2.5 p-3.5 rounded-xl border font-bold text-xs uppercase tracking-wider transition ${metodoPagamento === 'pix' ? 'border-[#ff2d6c] bg-pink-50/40 text-[#ff2d6c]' : 'border-slate-200 text-slate-500 bg-white hover:bg-slate-50'}`}
                          >
                            <QrCode size={16} />
                            <span>Pix</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setMetodoPagamento('cartao')}
                            className={`flex items-center justify-center gap-2.5 p-3.5 rounded-xl border font-bold text-xs uppercase tracking-wider transition ${metodoPagamento === 'cartao' ? 'border-[#ff2d6c] bg-pink-50/40 text-[#ff2d6c]' : 'border-slate-200 text-slate-500 bg-white hover:bg-slate-50'}`}
                          >
                            <CreditCard size={16} />
                            <span>Cartão</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Campos do Cartão */}
                    {metodoPagamento === 'cartao' && !evento.pixOnly && (
                      <div className="space-y-3 pt-3 border-t border-slate-100 animate-fadeIn">
                        <div>
                          <input
                            value={cartao.numero}
                            onChange={(e) =>
                              atualizarCartao('numero', e.target.value)
                            }
                            placeholder="Número do cartão"
                            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#ff2d6c]/10 focus:border-[#ff2d6c] bg-slate-50/50"
                          />
                          {erros.cartaoNumero && (
                            <p className="text-xs text-red-500 mt-1">
                              {erros.cartaoNumero}
                            </p>
                          )}
                        </div>

                        <div>
                          <input
                            value={cartao.nome}
                            onChange={(e) =>
                              atualizarCartao('nome', e.target.value)
                            }
                            placeholder="Nome impresso no cartão"
                            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#ff2d6c]/10 focus:border-[#ff2d6c] bg-slate-50/50"
                          />
                          {erros.cartaoNome && (
                            <p className="text-xs text-red-500 mt-1">
                              {erros.cartaoNome}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <input
                            value={cartao.validade}
                            onChange={(e) =>
                              atualizarCartao('validade', e.target.value)
                            }
                            placeholder="MM/AA"
                            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#ff2d6c]/10 focus:border-[#ff2d6c] bg-slate-50/50"
                          />
                          <input
                            value={cartao.cvv}
                            onChange={(e) =>
                              atualizarCartao('cvv', e.target.value)
                            }
                            placeholder="CVV"
                            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#ff2d6c]/10 focus:border-[#ff2d6c] bg-slate-50/50"
                          />
                        </div>
                        {(erros.cartaoValidade || erros.cartaoCvv) && (
                          <p className="text-xs text-red-500 font-medium">
                            Verifique os dados de validade e CVV do cartão.
                          </p>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <CampoCupom
                        cupom={cupom}
                        validar={validarCupom}
                        onAplicar={setCupom}
                        onRemover={() => setCupom(null)}
                      />
                      {cupom && (
                        <div className="flex justify-between text-sm font-bold text-emerald-700 tabular-nums">
                          <span>Desconto</span>
                          <span>− {REAIS(cupom.descontoCentavos)}</span>
                        </div>
                      )}
                    </div>

                    {/* Erro Global */}
                    {erroGlobal && (
                      <p className="text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                        {erroGlobal}
                      </p>
                    )}

                    <AceiteTermosCondicoes
                      aceito={termosAceitos}
                      onChange={setTermosAceitos}
                    />

                    {/* Botão de Enviar */}
                    <button
                      type="submit"
                      disabled={enviando || !termosAceitos}
                      className="w-full flex items-center justify-center gap-2 bg-[#ff2d6c] text-white py-4 rounded-full text-xs font-black uppercase tracking-widest hover:bg-[#e02059] disabled:opacity-50 transition shadow-lg shadow-pink-500/20 active:scale-[0.99]"
                    >
                      {enviando ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        `Garantir minha vaga • ${REAIS(evento.amountInCents - (cupom?.descontoCentavos ?? 0))}`
                      )}
                    </button>
                  </form>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
